import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { PINE_EXAMPLES } from '../pine/examples/index'
import { compilePine } from '../pine/index'

export interface PineScript {
  id: string
  title: string
  description: string
  source: string
  /** Compiled JS — regenerated on every save */
  compiledCode: string
  overlay: boolean
  enabled: boolean
  createdAt: number
  updatedAt: number
}

export interface PineCompileError {
  message: string
  line?: number
  col?: number
}

interface PineState {
  scripts: PineScript[]

  addScript: (title: string, description: string, source: string) => string
  updateScript: (
    id: string,
    patch: Partial<Pick<PineScript, 'title' | 'description' | 'source' | 'enabled'>>
  ) => PineCompileError[]
  removeScript: (id: string) => void
  duplicateScript: (id: string) => string | null
  toggleEnabled: (id: string) => void
  importScripts: (scripts: PineScript[]) => void
}

const EMPTY_CODE = `//@version=5
indicator("My Indicator", overlay=false)
smaVal = ta.sma(close, 20)
plot(smaVal, title="SMA 20", color="#2962ff")
`

function compileAndExtract(
  title: string,
  source: string
): { code: string; overlay: boolean; resolvedTitle: string } {
  const result = compilePine(source)
  return {
    code: result.code,
    overlay: result.overlay,
    resolvedTitle: result.title || title,
  }
}

export const usePineStore = create<PineState>()(
  persist(
    immer((set) => ({
      scripts: [],

      addScript(title, description, source) {
        const id = crypto.randomUUID()
        const { code, overlay, resolvedTitle } = compileAndExtract(title, source)
        set((s) => {
          s.scripts.push({
            id,
            title: resolvedTitle || title,
            description,
            source,
            compiledCode: code,
            overlay,
            enabled: true,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          })
        })
        return id
      },

      updateScript(id, patch) {
        const errors: PineCompileError[] = []
        set((s) => {
          const idx = s.scripts.findIndex((sc) => sc.id === id)
          if (idx === -1) return
          const script = s.scripts[idx]
          const newSource = patch.source ?? script.source
          const newTitle = patch.title ?? script.title

          if (patch.source !== undefined) {
            const result = compilePine(newSource)
            if (result.errors.length > 0) {
              errors.push(...result.errors)
            }
            script.compiledCode = result.code
            script.overlay = result.overlay
            if (result.title) script.title = result.title
          }

          if (patch.title !== undefined) script.title = newTitle
          if (patch.description !== undefined) script.description = patch.description
          if (patch.enabled !== undefined) script.enabled = patch.enabled
          script.source = newSource
          script.updatedAt = Date.now()
        })
        return errors
      },

      removeScript(id) {
        set((s) => {
          s.scripts = s.scripts.filter((sc) => sc.id !== id)
        })
      },

      duplicateScript(id) {
        let newId: string | null = null
        set((s) => {
          const script = s.scripts.find((sc) => sc.id === id)
          if (!script) return
          const generatedId = crypto.randomUUID()
          newId = generatedId
          s.scripts.push({
            ...script,
            id: generatedId,
            title: `${script.title} (copy)`,
            createdAt: Date.now(),
            updatedAt: Date.now(),
          })
        })
        return newId
      },

      toggleEnabled(id) {
        set((s) => {
          const script = s.scripts.find((sc) => sc.id === id)
          if (script) script.enabled = !script.enabled
        })
      },

      importScripts(scripts) {
        set((s) => {
          for (const sc of scripts) {
            const existing = s.scripts.findIndex((x) => x.id === sc.id)
            if (existing >= 0) {
              s.scripts[existing] = { ...sc, updatedAt: Date.now() }
            } else {
              s.scripts.push(sc)
            }
          }
        })
      },
    })),
    { name: 'mhp-pine-scripts', version: 1 }
  )
)

// ── Default template for new Pine scripts ─────────────────────────────────────
export const DEFAULT_PINE_CODE = EMPTY_CODE

// ── Load built-in examples into store if empty (called once on app start) ────
export function seedPineExamplesIfEmpty(store: ReturnType<typeof usePineStore.getState>) {
  if (store.scripts.length > 0) return
  for (const ex of PINE_EXAMPLES) {
    store.addScript(ex.title, ex.description, ex.code)
  }
}
